import { Card, CardContent, Typography, Box, LinearProgress } from '@mui/material';

interface BreakEvenDisplayProps {
    recoupMonths: {
        conservative: number;
        moderate: number;
        optimistic: number;
    };
    standardTotal: number;
}

const scenarioColors: Record<string, string> = {
    conservative: '#ff9800',
    moderate: '#2196f3',
    optimistic: '#4caf50',
};

const formatMonths = (months: number): string => {
    if (months === -1) return 'Never (within 36 months)';
    if (months === 0) return 'Immediate';
    if (months === 1) return '1 month';
    return `${months} months`;
};

export const BreakEvenDisplay = ({
    recoupMonths,
    standardTotal,
}: BreakEvenDisplayProps) => {
    const maxMonths = 36;

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Recoup Timeline
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                    How long until you earn back {`$${standardTotal.toLocaleString()}`} (your standard rate)
                </Typography>

                {(['optimistic', 'moderate', 'conservative'] as const).map(
                    (scenario) => {
                        const months = recoupMonths[scenario];
                        const progress =
                            months === -1
                                ? 100
                                : months === 0
                                  ? 0
                                  : (months / maxMonths) * 100;

                        return (
                            <Box key={scenario} mb={2}>
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                        {scenario}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        color={months === -1 ? 'error' : 'textPrimary'}
                                    >
                                        {formatMonths(months)}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, progress)}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: '#e0e0e0',
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor:
                                                months === -1
                                                    ? '#f44336'
                                                    : scenarioColors[scenario],
                                            borderRadius: 4,
                                        },
                                    }}
                                />
                            </Box>
                        );
                    }
                )}
            </CardContent>
        </Card>
    );
};
